import { OperationOutcome } from "@medplum/fhirtypes";

import { getMedplumErrorStatus } from "./medplum-http-error";

type ErrorWithOutcome = {
  name?: string;
  outcome?: OperationOutcome;
};

// The kind of failure, taken from the error's own class name. Never its message: a FHIR error message
// quotes the resource the server rejected.
const getErrorType = (error: unknown): string => {
  if (error instanceof Error) return error.name;
  return typeof error;
};

// FHIR issue severity and code come from fixed value sets ("error", "invalid", "processing"), so they
// say what kind of failure it was without quoting the request. The rest of the issue is dropped:
// diagnostics, details.text, and expression all echo values from the resources that were sent.
const getOutcomeIssues = (error: unknown): string[] => {
  const outcome = (error as ErrorWithOutcome | null)?.outcome;

  return (outcome?.issue ?? []).map((issue) => `${issue.severity}/${issue.code}`);
};

/**
 * Reads the non-PHI facts about a failure: its type, HTTP status, OperationOutcome id, and issue codes.
 *
 * Only the keys that have a value are included. The result never carries text from the error's
 * message, diagnostics, or details.
 *
 * @param error - The caught error. Read for metadata only; nothing from it is copied as free text.
 * @returns The error's type, plus `status`, `outcomeId`, and `outcomeIssues` when the error has them.
 */
export const getPhiSafeErrorMetadata = (error: unknown): PhiSafeErrorMetadata => {
  const status = getMedplumErrorStatus(error);
  const outcomeId = (error as ErrorWithOutcome | null)?.outcome?.id;
  const outcomeIssues = getOutcomeIssues(error);

  return {
    errorType: getErrorType(error),
    ...(status ? { status } : {}),
    ...(outcomeId ? { outcomeId } : {}),
    ...(outcomeIssues.length ? { outcomeIssues } : {}),
  };
};

export type PhiSafeErrorMetadata = {
  errorType: string;
  status?: number;
  outcomeId?: string;
  outcomeIssues?: string[];
};
